/** 與 LiffAuth 相同：使用 CDN 的 window.liff，避免 @line/liff 與 access.line.me 登入狀態不同步 */
const LIFF_SDK_URL = 'https://static.line-scdn.net/liff/edge/2/sdk.js';
const HARD_CODED_LIFF_ID = '2007543734-5n4NpyXV';

function ensureLiffSdk() {
  if (typeof window === 'undefined') return Promise.reject(new Error('NO_WINDOW'));
  if (window.liff) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existed = document.querySelector(`script[src="${LIFF_SDK_URL}"]`);
    if (existed) {
      existed.addEventListener('load', () => resolve());
      existed.addEventListener('error', () => reject(new Error('LOAD_LIFF_SDK_FAIL')));
      return;
    }
    const s = document.createElement('script');
    s.src = LIFF_SDK_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('LOAD_LIFF_SDK_FAIL'));
    document.head.appendChild(s);
  });
}

async function getLiffInstance() {
  await ensureLiffSdk();
  return window.liff;
}

function readStoredLineSub() {
  try {
    const sub = (localStorage.getItem('line_sub') || '').trim();
    return sub || null;
  } catch {
    return null;
  }
}

/** LINE App 內輪詢取得 sub（不依賴 F12） */
export async function waitForLineSub({ hint = null, maxWaitMs = 6000 } = {}) {
  if (hint) {
    try { localStorage.setItem('line_sub', hint); } catch { /* ignore */ }
    return hint;
  }
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const stored = readStoredLineSub();
    if (stored) return stored;
    try {
      const inst = typeof window !== 'undefined' ? window.liff : null;
      if (inst?.isLoggedIn?.()) {
        const sub = inst.getDecodedIDToken?.()?.sub || null;
        if (sub) {
          try { localStorage.setItem('line_sub', sub); } catch { /* ignore */ }
          return sub;
        }
      }
    } catch { /* ignore */ }
    await new Promise((resolve) => { setTimeout(resolve, 300); });
  }
  return readStoredLineSub();
}

export function getLineIdToken() {
  try {
    if (window.liff?.isLoggedIn?.() && typeof window.liff.getIDToken === 'function') {
      return window.liff.getIDToken() || null;
    }
  } catch { /* ignore */ }
  return null;
}
const AUTO_LINE_KEY = 'couponClaim:autoLine';
const PENDING_CLAIM_KEY = 'couponClaim:pendingResult';
const COUPON_CODE_KEY = 'couponClaim:code';
const AWAIT_LIFF_LOGIN_KEY = 'couponClaim:awaitLiffLogin';

const LIFF_QUERY_KEYS = [
  'liff.state',
  'liff.referrer',
  'liffClientId',
  'liffSdkVersion',
  'liffChannelId',
  'liff.subWindow',
  'liff.referrerAppId',
];

export function getLiffId() {
  return (process.env.REACT_APP_LIFF_ID || window.__LIFF_ID__ || HARD_CODED_LIFF_ID).trim();
}

export function buildCouponClaimPath(code, { lineClaim = false } = {}) {
  const c = String(code || '').trim();
  const usp = new URLSearchParams();
  if (c) usp.set('code', c);
  if (lineClaim) usp.set('lineClaim', '1');
  const q = usp.toString();
  return `/coupon-claim${q ? `?${q}` : ''}`;
}

export function buildCouponLiffUrl(code) {
  const liffId = getLiffId();
  const state = encodeURIComponent(buildCouponClaimPath(code, { lineClaim: true }));
  return {
    universal: `https://liff.line.me/${liffId}?liff.state=${state}`,
    scheme: `line://app/${liffId}?liff.state=${state}`,
  };
}

export function buildCouponLinkReturnUrl(code) {
  return `/link?from=${encodeURIComponent(buildCouponClaimPath(code, { lineClaim: true }))}`;
}

export function setAutoLineClaimFlag() {
  try { sessionStorage.setItem(AUTO_LINE_KEY, '1'); } catch { /* ignore */ }
}

export function clearAutoLineClaimFlag() {
  try { sessionStorage.removeItem(AUTO_LINE_KEY); } catch { /* ignore */ }
}

export function hasAutoLineClaimFlag() {
  try { return sessionStorage.getItem(AUTO_LINE_KEY) === '1'; } catch { return false; }
}

export function savePendingClaimResult(code, claim) {
  if (!code || !claim) return;
  try {
    sessionStorage.setItem(PENDING_CLAIM_KEY, JSON.stringify({ code, claim }));
  } catch { /* ignore */ }
}

export function loadPendingClaimResult(code) {
  if (!code) return null;
  try {
    const raw = sessionStorage.getItem(PENDING_CLAIM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.code !== code) return null;
    return parsed.claim || null;
  } catch {
    return null;
  }
}

export function clearPendingClaimResult() {
  try { sessionStorage.removeItem(PENDING_CLAIM_KEY); } catch { /* ignore */ }
}

export function setCouponLiffPendingLogin() {
  try { sessionStorage.setItem(AWAIT_LIFF_LOGIN_KEY, '1'); } catch { /* ignore */ }
}

export function clearCouponLiffPendingLogin() {
  try { sessionStorage.removeItem(AWAIT_LIFF_LOGIN_KEY); } catch { /* ignore */ }
}

export function hasCouponLiffPendingLogin() {
  try { return sessionStorage.getItem(AWAIT_LIFF_LOGIN_KEY) === '1'; } catch { return false; }
}

function stripLiffQueryParams(search) {
  const usp = new URLSearchParams(search || '');
  LIFF_QUERY_KEYS.forEach((key) => usp.delete(key));
  const fromVal = usp.get('from');
  if (fromVal && fromVal.toLowerCase() === 'line') usp.delete('from');
  return usp;
}

export function persistCouponClaimCode(code) {
  const c = String(code || '').trim();
  if (!c) return;
  try { sessionStorage.setItem(COUPON_CODE_KEY, c); } catch { /* ignore */ }
}

export function loadPersistedCouponClaimCode() {
  try {
    return (sessionStorage.getItem(COUPON_CODE_KEY) || '').trim();
  } catch {
    return '';
  }
}

function parseCodeFromSearch(search) {
  return (new URLSearchParams(search || '').get('code') || '').trim();
}

function parseCodeFromLiffState(raw) {
  if (!raw) return '';
  try {
    let decoded = raw;
    for (let i = 0; i < 2; i += 1) {
      if (!decoded.includes('%')) break;
      try { decoded = decodeURIComponent(decoded); } catch { break; }
    }
    const target = decoded.startsWith('/') ? decoded : `/${decoded}`;
    const qIdx = target.indexOf('?');
    const qs = qIdx >= 0 ? target.slice(qIdx + 1) : '';
    const fromQs = (new URLSearchParams(qs).get('code') || '').trim();
    if (fromQs) return fromQs;
    return extractCouponCodeFromReturnUrl(target);
  } catch {
    return '';
  }
}

function parseCodeFromFromParam(search) {
  const from = (new URLSearchParams(search || '').get('from') || '').trim();
  if (!from) return '';
  try {
    const decoded = decodeURIComponent(from);
    if (!decoded.includes('coupon-claim')) return '';
    return extractCouponCodeFromReturnUrl(decoded);
  } catch {
    return extractCouponCodeFromReturnUrl(from);
  }
}

function decodeLiffStatePath(raw) {
  if (!raw) return null;
  try {
    let decoded = raw;
    for (let i = 0; i < 2; i += 1) {
      if (!decoded.includes('%')) break;
      try { decoded = decodeURIComponent(decoded); } catch { break; }
    }
    return decoded.startsWith('/') ? decoded : `/${decoded}`;
  } catch {
    return null;
  }
}

function buildCleanCouponClaimPathFromLiffState(liffStateRaw) {
  const target = decodeLiffStatePath(liffStateRaw);
  if (!target || !target.includes('/coupon-claim')) return null;
  const qIdx = target.indexOf('?');
  const pathname = qIdx >= 0 ? target.slice(0, qIdx) : target;
  const search = qIdx >= 0 ? target.slice(qIdx) : '';
  const merged = new URLSearchParams(search.replace(/^\?/, ''));
  const code = (merged.get('code') || '').trim() || parseCodeFromLiffState(liffStateRaw);
  if (code) merged.set('code', code);
  merged.set('lineClaim', '1');
  const query = merged.toString();
  return `${pathname}${query ? `?${query}` : ''}`;
}

/** URL 是否表示「從 LINE / LIFF 進來要自動領券」 */
export function hasLineClaimIntent() {
  if (typeof window === 'undefined') return false;
  const usp = new URLSearchParams(window.location.search);
  if (usp.get('lineClaim') === '1') return true;
  if (usp.get('liff.state')) return true;
  if ((usp.get('from') || '').toLowerCase() === 'line') return true;
  return false;
}

/** 一般領券連結（僅 ?code=）進站時清掉殘留的 autoLine，避免未按鈕就跳 LINE */
export function prepareCouponClaimDirectVisit() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/coupon-claim') return;
  if (hasLineClaimIntent()) return;
  clearAutoLineClaimFlag();
}

/** 解析是否應導向領券頁（僅 liff.state） */
export function resolveCouponClaimRedirectTarget() {
  if (typeof window === 'undefined') return null;
  const usp = new URLSearchParams(window.location.search);
  return buildCleanCouponClaimPathFromLiffState(usp.get('liff.state'));
}

/**
 * LIFF Endpoint 為 / 時，進站前整頁導向 /coupon-claim（React 掛載前呼叫）。
 * @returns {boolean} true 表示已觸發 redirect，不應再 render React
 */
export function redirectIfCouponLiffLanding() {
  if (typeof window === 'undefined') return false;
  const targetPath = resolveCouponClaimRedirectTarget();
  if (!targetPath) return false;

  if (window.location.pathname === '/coupon-claim') {
    const usp = new URLSearchParams(window.location.search);
    if (usp.get('liff.state') || LIFF_QUERY_KEYS.some((k) => usp.has(k))) {
      const code = parseCodeFromSearch(window.location.search)
        || parseCodeFromLiffState(usp.get('liff.state'));
      if (code) {
        persistCouponClaimCode(code);
        window.location.replace(buildCouponClaimPath(code, { lineClaim: true }));
        return true;
      }
    }
    return false;
  }

  const code = extractCouponCodeFromReturnUrl(targetPath);
  if (code) persistCouponClaimCode(code);
  window.location.replace(targetPath);
  return true;
}

/** LiffAuth 備援：整頁導向領券頁（僅 liff.state） */
export function redirectToCouponClaimIfNeeded() {
  const targetPath = resolveCouponClaimRedirectTarget();
  if (!targetPath) return false;
  const current = `${window.location.pathname}${window.location.search}`;
  if (current === targetPath) return false;
  const code = extractCouponCodeFromReturnUrl(targetPath);
  if (code) persistCouponClaimCode(code);
  window.location.replace(targetPath);
  return true;
}

/** 還原 liff.state 並解析 coupon code（URL → liff.state → from → sessionStorage） */
export function resolveCouponClaimCode() {
  if (typeof window === 'undefined') return '';
  restoreLiffState();
  const search = window.location.search;
  let code = parseCodeFromSearch(search);
  if (code) {
    persistCouponClaimCode(code);
    return code;
  }
  code = parseCodeFromLiffState(new URLSearchParams(search).get('liff.state'));
  if (code) {
    persistCouponClaimCode(code);
    return code;
  }
  code = parseCodeFromFromParam(search);
  if (code) {
    persistCouponClaimCode(code);
    return code;
  }
  return loadPersistedCouponClaimCode();
}

/** 還原 liff.state 到 pathname+search，回傳是否已還原 */
export function restoreLiffState() {
  const usp = new URLSearchParams(window.location.search);
  const liffState = usp.get('liff.state');
  if (!liffState) return null;

  try {
    const decoded = decodeURIComponent(liffState);
    const target = decoded.startsWith('/') ? decoded : `/${decoded}`;
    const qIdx = target.indexOf('?');
    const pathname = qIdx >= 0 ? target.slice(0, qIdx) : target;
    const search = qIdx >= 0 ? target.slice(qIdx) : '';
    const cleanUsp = stripLiffQueryParams(window.location.search);
    const merged = new URLSearchParams(search.replace(/^\?/, ''));
    cleanUsp.forEach((v, k) => {
      if (!merged.has(k)) merged.set(k, v);
    });
    const query = merged.toString();
    const finalPath = `${pathname}${query ? `?${query}` : ''}`;
    window.history.replaceState({}, '', finalPath);
    const code = parseCodeFromSearch(`?${query}`) || parseCodeFromLiffState(liffState);
    if (code) persistCouponClaimCode(code);
    return finalPath;
  } catch {
    return null;
  }
}

/** 是否在 LINE App 內走「自動領券」UI（非一般瀏覽器 Email 表單） */
export function isCouponLineClaimMode() {
  if (!hasLineClaimIntent()) return false;
  if (isLineInApp()) return true;
  try {
    const inst = typeof window !== 'undefined' ? window.liff : null;
    if (typeof inst?.isInClient === 'function' && inst.isInClient() === true) return true;
  } catch { /* LIFF 尚未 init */ }
  const usp = new URLSearchParams(window.location.search);
  return !!usp.get('liff.state');
}

/** 僅在 LINE 領券模式時自動 claim-by-line；一般 ?code= 不自動 */
export function shouldAutoLineClaim() {
  return isCouponLineClaimMode();
}

/** 外部瀏覽器：只開 LIFF 連結進 LINE App，不在頁內 init LIFF */
export function openCouponClaimInLineApp(code) {
  if (code) persistCouponClaimCode(code);
  const links = buildCouponLiffUrl(code);
  window.location.assign(links.scheme);
  setTimeout(() => {
    if (document.visibilityState !== 'hidden') {
      window.location.assign(links.universal);
    }
  }, 800);
}

function isLineInAppUa() {
  const ua = (navigator.userAgent || '').toLowerCase();
  return ua.includes(' line') || ua.includes('liff');
}

/** LINE 內建瀏覽器或 LIFF：優先 liff.isInClient，fallback UA */
export function isLineInApp() {
  try {
    const inst = typeof window !== 'undefined' ? window.liff : null;
    if (typeof inst?.isInClient === 'function' && inst.isInClient() === true) return true;
  } catch { /* LIFF 尚未 init */ }
  return isLineInAppUa();
}

export async function detectLineInApp() {
  try {
    const inst = await initLiffForCoupon();
    if (inst.isInClient?.() === true) return true;
  } catch { /* ignore */ }
  return isLineInAppUa();
}

export async function initLiffForCoupon() {
  const inst = await getLiffInstance();
  await inst.init({ liffId: getLiffId(), withLoginOnExternalBrowser: true });
  return inst;
}

/** 僅在 LINE App 內：尚未登入 LIFF 時觸發 liff.login */
export async function ensureLiffLoginForCouponClaim(code) {
  if (code) persistCouponClaimCode(code);
  const redirectUri = code
    ? `${window.location.origin}${buildCouponClaimPath(code, { lineClaim: true })}`
    : `${window.location.origin}/coupon-claim?lineClaim=1`;

  const inst = await initLiffForCoupon();
  const inLine = inst.isInClient?.() === true || isLineInAppUa();
  if (!inLine) {
    return { redirected: false, loggedIn: false, notInLine: true };
  }
  if (!inst.isLoggedIn()) {
    setCouponLiffPendingLogin();
    inst.login({ redirectUri });
    return { redirected: true };
  }
  clearCouponLiffPendingLogin();
  return { redirected: false, loggedIn: true };
}

/** @deprecated 外部請用 openCouponClaimInLineApp；LINE 內請用 ensureLiffLoginForCouponClaim */
export async function startLineLoginForCoupon(code) {
  return ensureLiffLoginForCouponClaim(code);
}

export async function getLineSubFromLiff() {
  const stored = readStoredLineSub();
  const inst = await initLiffForCoupon();
  if (!inst.isLoggedIn()) return stored;
  const decoded = inst.getDecodedIDToken?.();
  return decoded?.sub || stored || null;
}

const LIFF_INIT_TIMEOUT_MS = 8000;

async function initLiffForCouponWithTimeout() {
  await Promise.race([
    initLiffForCoupon(),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('LIFF_INIT_TIMEOUT')), LIFF_INIT_TIMEOUT_MS);
    }),
  ]);
}

/**
 * 解析 LINE 領券用 sub：未登入立即回傳；已登入才短暫重試 idToken。
 * @returns {{ sub: string|null, loggedIn: boolean, inClient: boolean }}
 */
export async function resolveLineSubForCouponClaim() {
  const stored = readStoredLineSub();
  let inClient = isLineInAppUa();
  let inst;
  try {
    inst = await initLiffForCouponWithTimeout();
    inClient = inst.isInClient?.() === true || isLineInAppUa();
  } catch {
    return { sub: stored, loggedIn: !!stored, inClient };
  }

  if (!inst.isLoggedIn?.()) {
    return { sub: null, loggedIn: false, inClient };
  }

  let sub = inst.getDecodedIDToken?.()?.sub || stored || null;
  if (sub) {
    try { localStorage.setItem('line_sub', sub); } catch { /* ignore */ }
    clearCouponLiffPendingLogin();
    return { sub, loggedIn: true, inClient };
  }

  for (let i = 0; i < 6; i += 1) {
    await new Promise((resolve) => { setTimeout(resolve, 350); });
    try {
      sub = inst.getDecodedIDToken?.()?.sub || readStoredLineSub() || null;
      if (sub) {
        try { localStorage.setItem('line_sub', sub); } catch { /* ignore */ }
        clearCouponLiffPendingLogin();
        return { sub, loggedIn: true, inClient };
      }
    } catch { /* ignore */ }
  }

  return { sub: stored, loggedIn: true, inClient };
}

/** @deprecated 請用 resolveLineSubForCouponClaim */
export async function getLineSubFromLiffWithRetry() {
  const { sub } = await resolveLineSubForCouponClaim();
  return sub;
}

export function saveCouponClaimDraft(code, draft) {
  if (!code) return;
  try {
    sessionStorage.setItem(`couponClaim:${code}`, JSON.stringify(draft));
  } catch { /* ignore */ }
}

export function loadCouponClaimDraft(code) {
  if (!code) return null;
  try {
    const raw = sessionStorage.getItem(`couponClaim:${code}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearCouponClaimDraft(code) {
  if (!code) return;
  try {
    sessionStorage.removeItem(`couponClaim:${code}`);
  } catch { /* ignore */ }
}

export function extractCouponCodeFromReturnUrl(returnUrl) {
  const raw = String(returnUrl || '').trim();
  if (!raw) return '';
  try {
    const path = raw.startsWith('http') ? new URL(raw).search : raw.includes('?') ? raw.slice(raw.indexOf('?')) : '';
    const usp = new URLSearchParams(path.startsWith('?') ? path.slice(1) : path);
    return (usp.get('code') || '').trim();
  } catch {
    const m = raw.match(/[?&]code=([^&]+)/);
    return m ? decodeURIComponent(m[1]).trim() : '';
  }
}

export function maskEmailForDisplay(email) {
  const e = String(email || '').trim();
  if (!e || !e.includes('@')) return e;
  const [local, domain] = e.split('@');
  return `${local.slice(0, 1)}***@${domain}`;
}
